/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { activate } from "./activate";
import { SHOW_ERRORS, ErrorPreview } from "./ErrorPreview";
import {
  COVER_IMAGE_TYPES,
  COVER_POSITION_DEFAULT,
  COVER_ZOOM_MAX,
  coverFrameStyle,
  normalizeCoverPosition,
  sameCoverPosition,
} from "../data";
import InfoTip from "./InfoTip";
import "./NonprofitProof.css";

// The one string here that takes a value. Everything else the view renders is
// filled in the view model.
const fill = (template, values) =>
  Object.keys(values).reduce(
    (out, key) => out.split("{" + key + "}").join(values[key]),
    template || ""
  );

// Nonprofit · publishing, in the order a supporter reads it: the fundraising
// goal, part 1's key metrics, part 2's what-a-giving-buys, and part 3's
// What's new feed. The page is an editing surface by default and flips to the
// supporter's view via "Preview public page".
//
// Every section is the same shape — a head with a "+ Add …" button, a form
// that only that button opens, a one-line subhead with an "Edit …" link, and
// then the rows. Only one form is ever open, so there is never more than one
// place where something typed could be lost.
//
// Each section loads from its own endpoint and shows its own skeleton, its own
// failure and its own retry, inside its own card. One section being down
// leaves the other three readable and editable — which is the whole reason
// they were split apart, after a report link that could not be signed took the
// tiles, the giving-buys rows and the feed down with it.
//
// The only state this component keeps is the files picked in the composer: a
// File cannot be serialised into the page's view model, and they exist for the
// few seconds between choosing them and the post they belong to being written.
class NonprofitProof extends Component {
  constructor(props) {
    super(props);
    // ytPlaying is the preview's own concern -- whether the thumbnail has
    // been swapped for the player. metricReport is the document picked in
    // part 1's upload block.
    this.state = {
      // The logo URL whose image has finished loading. Until the tile's
      // current logo is this one, the tile shows it is still working; see
      // logoBusy.
      logoLoaded: "",
      photos: [],
      report: null,
      ytPlaying: false,
      metricReport: null,
      // Chosen in the modal, not yet sent: Cancel has to leave the cover
      // alone, so nothing is uploaded until "Save image".
      coverDraft: null,
      coverDraftUrl: "",
      coverOver: false,
      // The framing being edited in the modal, or null while it is still the
      // stored one (CP-374).
      coverFrame: null,
      // The introduction reads as a paragraph until "Edit introduction" is
      // pressed, as the artboard has it -- the profile page's field is always
      // open instead.
      introEditing: false,
      // The cover URL that would not load, if one would not. Stored as the
      // URL rather than a flag so a new upload clears it by being a different
      // URL, with nothing to reset.
      coverError: "",
    };
    // Part 3 sits below the goal, part 1 and part 2, so "New update" from a
    // section header opens a composer that is off-screen. scrollToComposer
    // brings it into view; see componentDidMount.
    this.postForm = React.createRef();
    this.coverInput = React.createRef();
    // The modal's image area, measured while a cover is dragged.
    this.coverZone = React.createRef();
    this.photoInput = React.createRef();
    this.reportInput = React.createRef();
    this.metricReportInput = React.createRef();
  }

  /**
   * The picked files belong to one composer session, and only the parent knows
   * when that session ends: closing the composer abandons the draft, and "Edit"
   * on an existing post replaces what is being written. Both blank the written
   * fields up there, and files that outlived them were uploaded onto whatever
   * was written next — a fresh post carrying the abandoned draft's photos, or
   * an unrelated post being edited and quietly gaining them.
   *
   * A failed save is not one of these: the composer stays open on the same
   * post, so what was attached to it survives, as submitPost intends.
   */
  /**
   * "New update" is in every nonprofit section header, so it is pressed from
   * the dashboard, from supporters, from anywhere — and it lands on a
   * composer four cards down the impact page, below the fold. Arriving there
   * with the page still scrolled to the top reads as the button having done
   * nothing, which is what this exists to prevent.
   *
   * Mounting covers arriving from another section; the update below covers
   * pressing it while already on the impact page.
   */
  componentDidMount() {
    if (this.props.vm.proof.postOpen) {
      this.scrollToComposer();
    }
  }

  scrollToComposer = () => {
    const node = this.postForm.current;
    if (!node || typeof node.scrollIntoView !== "function") {
      return;
    }
    // Deferred a frame: the composer is being mounted in this same commit, so
    // its final position is not settled until the browser has laid it out.
    window.requestAnimationFrame(() => {
      // The ref is re-read rather than closed over — a composer closed inside
      // that frame would otherwise be scrolled to after it had gone.
      const current = this.postForm.current;
      if (current) {
        current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  componentDidUpdate(prevProps) {
    const before = prevProps.vm.proof;
    const after = this.props.vm.proof;
    const moved =
      before.postOpen !== after.postOpen || before.editingPostId !== after.editingPostId;
    if (moved && (this.state.photos.length || this.state.report)) {
      this.setState({ photos: [], report: null });
    }
    // Only on the way open. Closing it, or editing a different post while it
    // is already open, should leave the page where the organisation put it.
    if (!before.postOpen && after.postOpen) {
      this.scrollToComposer();
    }
    if (before.postVideoId !== after.postVideoId && this.state.ytPlaying) {
      this.setState({ ytPlaying: false });
    }
    if (before.coverModalOpen && !after.coverModalOpen) {
      if (this.state.coverDraftUrl) {
        URL.revokeObjectURL(this.state.coverDraftUrl);
      }
      if (this.state.coverDraftUrl || this.state.coverFrame) {
        this.setState({ coverDraft: null, coverDraftUrl: "", coverOver: false, coverFrame: null });
      }
    }
  }

  componentWillUnmount() {
    if (this.state.coverDraftUrl) {
      URL.revokeObjectURL(this.state.coverDraftUrl);
    }
  }

  // A file input only fires onChange when the selection changes, so picking
  // the same file twice in a row would do nothing unless the value is cleared.
  pick = (ref) => () => {
    if (ref.current) {
      ref.current.click();
    }
  };

  // The cover zone is a role="button" div, so Enter and Space have to open
  // the picker the way a click does.
  pickOnKey = (ref) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      this.pick(ref)();
    }
  };

  // A file dropped while a save or removal is running is not taken, but the
  // browser must still be stopped from opening it in the tab.
  ignoreDrop = (e) => {
    e.preventDefault();
    this.setState({ coverOver: false });
  };

  onCoverPicked = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    this.takeCover(file);
  };

  // An object URL is a live handle on the file, so the old one is released
  // before it is replaced and again when the modal closes -- otherwise every
  // picked-and-cancelled image stays in memory for the life of the page.
  takeCover = (file) => {
    if (!file) {
      return;
    }
    this.setState((s) => {
      if (s.coverDraftUrl) {
        URL.revokeObjectURL(s.coverDraftUrl);
      }
      // A new picture starts centred: the old framing was for another image.
      return {
        coverDraft: file,
        coverDraftUrl: URL.createObjectURL(file),
        coverFrame: { ...COVER_POSITION_DEFAULT },
      };
    });
  };

  dropCover = (e) => {
    e.preventDefault();
    this.setState({ coverOver: false });
    const file = e.dataTransfer && e.dataTransfer.files[0];
    // Dropped files are filtered by the same allowlist the picker offers, so
    // a dragged-in AVIF is refused here rather than at the far end of an
    // upload. It used to test /^image\//, which is every image format there
    // has ever been and two of which the server accepts.
    if (!file) {
      return;
    }
    if (COVER_IMAGE_TYPES.indexOf(file.type) >= 0) {
      this.takeCover(file);
      return;
    }
    // A refused drop is handed to the same validator "Save image" goes
    // through: it refuses the type before it uploads anything, and it is the
    // one place that says which types there are. Returning here instead left
    // the modal doing nothing at all for a dropped HEIC — no preview, no
    // error, nothing to tell the organisation the file had been looked at.
    this.props.vm.proof.onPickCover(file);
  };

  overCover = (e) => {
    e.preventDefault();
    if (!this.state.coverOver) {
      this.setState({ coverOver: true });
    }
  };

  leaveCover = () => this.setState({ coverOver: false });

  closeCoverModal = () => {
    if (this.state.coverDraftUrl) {
      URL.revokeObjectURL(this.state.coverDraftUrl);
    }
    this.setState({ coverDraft: null, coverDraftUrl: "", coverOver: false, coverFrame: null });
    this.props.vm.proof.closeCoverModal();
  };

  // A new file goes up with its framing; a stored cover that was only
  // re-framed sends just the position (CP-374).
  saveCover = () => {
    const p = this.props.vm.proof;
    const frame = this.coverFrameOf(p);
    if (this.state.coverDraft) {
      return p.onPickCover(this.state.coverDraft, frame);
    }
    if (this.coverDirty(p)) {
      return p.onSaveCoverPosition(frame);
    }
    return undefined;
  };

  /* --- framing the cover (CP-374) ---------------------------------------- */

  coverFrameOf = (p) => normalizeCoverPosition(this.state.coverFrame || p.coverPosition);

  // Something to save: a new file, or a framing that differs from the stored.
  coverDirty = (p) =>
    !!this.state.coverDraft ||
    (!!this.state.coverFrame && !sameCoverPosition(this.state.coverFrame, p.coverPosition));

  setCoverFrame = (next) => this.setState({ coverFrame: normalizeCoverPosition(next) });

  coverNaturalSize = (e) => {
    this.coverNatural = { w: e.target.naturalWidth, h: e.target.naturalHeight };
  };

  /**
   * How far the image overhangs the area at a zoom, in pixels. Dragging moves
   * the object-position percentage by the share of that overhang the pointer
   * has travelled, so the image follows the pointer exactly. Null until the
   * image's own size is known.
   */
  coverOverhang = (zoom) => {
    const box = this.coverZone.current;
    const n = this.coverNatural;
    if (!box || !n || !n.w || !n.h) {
      return null;
    }
    const cw = box.clientWidth;
    const ch = box.clientHeight;
    const base = Math.max(cw / n.w, ch / n.h);
    return { x: n.w * base * zoom - cw, y: n.h * base * zoom - ch };
  };

  startCoverDrag = (e) => {
    if (e.button !== undefined && e.button !== 0) {
      return;
    }
    this.coverDrag = {
      x0: e.clientX,
      y0: e.clientY,
      frame: this.coverFrameOf(this.props.vm.proof),
    };
    if (e.currentTarget.setPointerCapture && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  moveCoverDrag = (e) => {
    const d = this.coverDrag;
    const over = d && this.coverOverhang(d.frame.zoom);
    if (!over) {
      return;
    }
    // Dragging right shows more of the left of the image: x goes down.
    this.setCoverFrame({
      ...d.frame,
      x: over.x > 0 ? d.frame.x - ((e.clientX - d.x0) / over.x) * 100 : d.frame.x,
      y: over.y > 0 ? d.frame.y - ((e.clientY - d.y0) / over.y) * 100 : d.frame.y,
    });
  };

  endCoverDrag = () => {
    this.coverDrag = null;
  };

  onCoverZoom = (e) =>
    this.setCoverFrame({ ...this.coverFrameOf(this.props.vm.proof), zoom: Number(e.target.value) });

  resetCoverFrame = () => this.setCoverFrame(COVER_POSITION_DEFAULT);

  // Arrow keys move the image the way it is pointed, 2% a press (10% with
  // Shift), so the framing does not need a pointer.
  nudgeCover = (e) => {
    const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const m = moves[e.key];
    if (!m) {
      return;
    }
    e.preventDefault();
    const step = e.shiftKey ? 10 : 2;
    const f = this.coverFrameOf(this.props.vm.proof);
    this.setCoverFrame({ ...f, x: f.x - m[0] * step, y: f.y - m[1] * step });
  };

  pickFromPill = (e) => {
    e.stopPropagation();
    this.pick(this.coverInput)();
  };

  stopPointer = (e) => e.stopPropagation();

  /**
   * The cover is stored but will not load.
   *
   * Worth reporting rather than swallowing. It used to be painted as a CSS
   * background, which has no error event at all, so a cover whose URL 404s or
   * whose object is not readable rendered as an empty grey box -- identical
   * to having no cover, except that the "no cover yet" hint was suppressed
   * because a cover *was* stored. Nothing on the page said anything, and the
   * only way to tell the two apart was the network tab.
   *
   * The hint below now says so on screen. The URL would identify which of the
   * two it is, but console statements are commented out on release branches,
   * so it stays commented alongside them.
   */
  coverBroke = (url) => () => {
    if (this.state.coverError !== url) {
      // console.warn("Plug: the cover image could not be loaded from " + url);
      this.setState({ coverError: url });
    }
  };

  /**
   * Whether the logo tile should show it is working: while the upload and
   * the record write run, and then until the new image has actually loaded.
   * The second half is the wait nobody could see -- the save had finished,
   * the old picture was still there, and nothing said the new one was on its
   * way (CP-370). A failed load ends the wait as well; the spinner is not an
   * error message.
   */
  logoBusy = (id) =>
    !!id.logoUploading || (!!id.logo && this.state.logoLoaded !== id.logo);

  logoSettled = (url) => () => {
    if (this.state.logoLoaded !== url) {
      this.setState({ logoLoaded: url });
    }
  };

  // Painted as an <img> so a failure has somewhere to be caught, with the
  // stored-but-unloadable case saying so in the hint's place.
  // `position` is the stored framing (CP-374), applied the same way at every
  // size; see coverFrameStyle.
  renderCoverInto = (vm, url, className, position) => {
    const broken = !!url && this.state.coverError === url;
    return (
      <React.Fragment>
        {!!url && !broken && (
          <img
            className={className}
            src={url}
            alt=""
            style={coverFrameStyle(position)}
            onError={this.coverBroke(url)}
          />
        )}
        {(!url || broken) && (
          <span className="Marketplace-Proof-coverHint">
            {broken ? vm.m.proofCoverBroken : vm.m.proofCoverHint}
          </span>
        )}
      </React.Fragment>
    );
  };

  onPhotosPicked = (e) => {
    const picked = Array.prototype.slice.call(e.target.files);
    e.target.value = "";
    this.setState((s) => ({ photos: s.photos.concat(picked) }));
  };

  onReportPicked = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (file) {
      this.setState({ report: file });
    }
  };

  removePhoto = (index) => () =>
    this.setState((s) => ({ photos: s.photos.filter((_, i) => i !== index) }));

  removeReport = () => this.setState({ report: null });

  startIntroEdit = () => this.setState({ introEditing: true });

  saveIntro = () => {
    // The commit is what writes the intro, and it can fail. Closing the editor
    // first threw the draft away, so a failed save snapped the field back to
    // the old text with nothing on screen to say why.
    const done = this.props.vm.proof.identity.intro.onBlur();
    if (!done || typeof done.then !== "function") {
      this.setState({ introEditing: false });
      return done;
    }
    return done.then((ok) => {
      if (ok) {
        this.setState({ introEditing: false });
      }
      return ok;
    });
  };

  cancelIntro = () => {
    this.props.vm.proof.identity.cancelIntro();
    this.setState({ introEditing: false });
  };

  playYt = () => this.setState({ ytPlaying: true });

  stopYt = () => this.setState({ ytPlaying: false });

  onMetricReportPicked = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (file) {
      this.setState({ metricReport: file });
    }
  };

  removeMetricReport = () => this.setState({ metricReport: null });

  // The composer's files are cleared only once the post they belong to has
  // been written — a rejected post keeps what was attached to it.
  submitPost = () => {
    const result = this.props.vm.proof.addPost(this.state.photos, this.state.report)();
    if (result && result.then) {
      result.then((ok) => {
        if (ok) {
          this.setState({ photos: [], report: null });
        }
        return ok;
      });
    }
  };

  /**
   * The banner's lead word is set apart on the artboard. The copy carries a
   * {supporters} placeholder rather than assuming that word comes first --
   * in zh and cn it does not.
   *
   * A pack that has dropped the placeholder still renders: the body is
   * returned whole, without the set-apart lead word, rather than nothing.
   */
  renderFundingBody(vm) {
    const parts = (vm.m.proofFundingBody || "").split("{supporters}");
    if (parts.length !== 2) {
      return vm.m.proofFundingBody;
    }
    return (
      <React.Fragment>
        {parts[0]}
        <span className="Marketplace-Proof-bannerLink">
          {vm.m.proofFundingLink}
        </span>
        {parts[1]}
      </React.Fragment>
    );
  }

  /**
   * One always-on hint, by id (CP-308).
   *
   * Read through a guard rather than straight off the view model. These are
   * decoration -- InfoTip draws nothing at all for a tip with no copy behind
   * it -- so a caller that builds a view model without them should get a page
   * with no (i) buttons, not a page that fails to render. Several of this
   * file's own test fixtures are exactly that caller.
   *
   * That the real workspace does hand them over is covered from the other
   * side, in GuidedTour.test.js, which asserts the buttons are on the page.
   */
  tip(vm, id) {
    return (vm.tips || {})[id];
  }

  renderFormError(p, previewKeys) {
    if (SHOW_ERRORS && previewKeys) {
      return <ErrorPreview m={this.props.vm.m} className="Marketplace-Proof-formError" keys={previewKeys} />;
    }
    if (!p.formError) {
      return null;
    }
    return <div className="Marketplace-Proof-formError">{p.formError}</div>;
  }

  /**
   * The head every section shares: its label, an optional note, and the button
   * that opens its form. The button is the organisation's own control, so it
   * is absent from the supporter's view entirely.
   *
   * `opts` carries the two things only some sections want, as an object rather
   * than a seventh and eighth positional argument: `anchor` is the guided
   * tour's data-tour value for the add button (CP-308), and `tip` is an
   * always-on (i) beside the label. Both are absent from the supporter's view
   * along with everything else it has no use for.
   */
  renderHead(label, note, addLabel, onAdd, hideAdd, isOpen, opts) {
    const extra = opts || {};
    return (
      /* The tour's anchor lands on the add button when there is one and on
         this head when there is not. It used to sit only on the button, which
         is inside `!hideAdd` -- and the goal section hides that as soon as a
         goal exists. tourPending offers the tour to every account predating
         it, i.e. the ones that already have goals, so precisely those readers
         reached the step with no anchor on the page and got a centred card
         about a button they were not being shown. Either way exactly one
         element carries it, so the querySelector cannot pick the wrong one. */
      <div
        className="Marketplace-Proof-partHead"
        data-tour={
          hideAdd && extra.anchorFallback !== false ? extra.anchor : undefined
        }
      >
        <span className="Marketplace-Proof-part">{label}</span>
        {/* Independent of the add button. The goal section hides its own
            "+ Add a goal" the moment a goal exists, and a hint about writing
            a good goal is worth more once there is one to edit, not less --
            tied to hideAdd it vanished exactly when it started being
            useful. The supporter's view gets no tip passed at all. */}
        {extra.tip && <InfoTip tip={extra.tip} />}
        {note && <span className="Marketplace-cardNote">{note}</span>}
        {!hideAdd && (
          <div
            className={
              "Marketplace-Proof-addBtn" +
              (isOpen ? " Marketplace-Proof-addBtn--on" : "")
            }
            onClick={onAdd}
            data-tour={extra.anchor}
          >
            {addLabel}
          </div>
        )}
      </div>
    );
  }

  // The line under a section's form: what the section is, and the link that
  // puts controls on its rows. The goal has no such link -- its single row
  // carries Edit and Remove outright -- so it passes no label and this is the
  // sentence alone.
  renderSub(text, editLabel, onEdit, isPublic, anchor) {
    return (
      <div className="Marketplace-Proof-sectionSub">
        <span className="Marketplace-Proof-sectionSubText">{text}</span>
        {!isPublic && editLabel && (
          <span
            className="Marketplace-Proof-editLink"
            onClick={onEdit}
            data-tour={anchor}
          >
            {editLabel}
          </span>
        )}
      </div>
    );
  }

  /**
   * A section's body: its skeleton while it is loading, its failure and retry
   * if it could not be read, its empty state if there is nothing in it, or the
   * rows themselves.
   *
   * @param {Object} vm - The whole view model, for its strings
   * @param {Object} state - { loading, error, retry } for this section
   * @param {String} empty - What to say when the section is simply empty
   * @param {Boolean} isEmpty - Whether it is
   * @param {Function} rows - Renders the rows, called only when there are some
   * @return {Object} The section's body
   */
  renderBody(vm, state, empty, isEmpty, rows, skipPreview) {
    if (SHOW_ERRORS && !skipPreview) {
      return (
        <React.Fragment>
          <div className="Marketplace-Proof-sectionError">
            <ErrorPreview m={vm.m} className="Marketplace-Proof-sectionErrorText" keys={["proofSectionError"]} />
            <span className="Marketplace-Proof-retry">{vm.m.proofRetry}</span>
          </div>
          {this.renderBody(vm, Object.assign({}, state, { error: false }), empty, isEmpty, rows, true)}
        </React.Fragment>
      );
    }
    if (state.loading) {
      return <div className="Marketplace-Proof-sectionLoading">{vm.m.proofLoading}</div>;
    }
    if (state.error) {
      return (
        <div className="Marketplace-Proof-sectionError">
          <span className="Marketplace-Proof-sectionErrorText">
            {vm.m.proofSectionError}
          </span>
          <span className="Marketplace-Proof-retry" onClick={state.retry}>
            {vm.m.proofRetry}
          </span>
        </div>
      );
    }
    if (isEmpty) {
      return <div className="Marketplace-Proof-empty">{empty}</div>;
    }
    return rows();
  }

  /* --- the fundraising goal ---------------------------------------------- */

  renderGoalForm(vm, p) {
    return (
      <div className="Marketplace-Proof-form" data-tour="proof-goal-form">
        <div className="Marketplace-Proof-formTitle">{p.goalFormTitle}</div>
        <div className="Marketplace-Proof-editor Marketplace-Proof-editor--bare">
          <div className="Marketplace-Proof-field Marketplace-Proof-field--wide">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofGoalName}</div>
            <input
              className="Marketplace-Proof-input"
              type="text"
              maxLength={80}
              value={p.goalName}
              onChange={p.onGoalName}
              placeholder={vm.m.proofGoalNameHint}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--period">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofGoalTarget}</div>
            <input
              className="Marketplace-Proof-input"
              type="number"
              value={p.goalTarget}
              onChange={p.onGoalTarget}
              placeholder={vm.m.proofGoalTargetHint}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--period">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofGoalFrom}</div>
            <input
              className="Marketplace-Proof-input"
              type="date"
              value={p.goalStart}
              onChange={p.onGoalStart}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--period">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofGoalTo}</div>
            <input
              className="Marketplace-Proof-input"
              type="date"
              value={p.goalEnd}
              onChange={p.onGoalEnd}
            />
          </div>
          <div
            className={
              "Marketplace-btnPrimary Marketplace-Proof-addBtnInline" +
              (p.canSaveGoal && !p.saving ? "" : " Marketplace-btn--inert")
            }
            onClick={p.canSaveGoal && !p.saving ? p.saveGoal : undefined}
          >
            {p.saveGoalLabel}
          </div>
        </div>
        <div className="Marketplace-Proof-fieldLabel Marketplace-Proof-blockLabel">
          {vm.m.proofGoalRepeats}
        </div>
        <div className="Marketplace-Proof-chips">
          {p.goalCadences.map((c) => (
            <div
              className={
                "Marketplace-Proof-chip" +
                (c.active ? " Marketplace-Proof-chip--on" : "")
              }
              key={c.id}
              onClick={c.onPick}
            >
              {c.label}
            </div>
          ))}
        </div>
        {this.renderFormError(p, ["proofErrors.goalName", "proofErrors.goalTarget", "proofErrors.goalPeriod", "proofErrors.save", "proofErrors.noChange"])}
        <div className="Marketplace-Proof-formNote">{vm.m.proofGoalPublicNote}</div>
      </div>
    );
  }

  renderGoal(vm, p, isPublic) {
    // Edit and Remove sit on the goal row itself, always — there is one goal,
    // so there is nothing for an "Edit goals" switch to disambiguate, and the
    // row is the thing being acted on.
    const editing = !isPublic;
    return (
      <div className="Marketplace-card">
        {/* The head's button stays on the row whether or not a goal is set:
            there is one goal and a single-resource PUT behind it, so pressing
            it over an existing goal sets that goal from a blank form rather
            than adding a second. It is also what closes the form again — it
            reads "Close" while the form is open — and the row's Edit opens
            that same form on what is stored. */}
        {this.renderHead(
          vm.m.proofGoalTitle,
          vm.m.proofGoalHeadNote,
          p.goalAddLabel,
          p.toggleGoalAdd,
          isPublic,
          p.goalOpen,
          {
            anchor: "proof-goal-add",
            // The supporter's view has no add button and no tour to anchor,
            // so it is the one place this heading would pick the anchor up as
            // a fallback -- and it should not: a full-width heading leaves no
            // horizontal room for step 7's left-hand tour card either.
            anchorFallback: false,
            tip: isPublic ? null : this.tip(vm, "goalPanel"),
          }
        )}
        {!isPublic && p.goalOpen && this.renderGoalForm(vm, p)}
        {this.renderBody(vm, p.pageState, p.goalEmpty, !p.goal, () => (
          <div>
            {/* Sentence only: this section's controls are on its row. */}
            {this.renderSub(p.goalSub)}
            <div className="Marketplace-Proof-goalHead">
              <span className="Marketplace-Proof-goalName">{p.goal.name}</span>
              <span className="Marketplace-Proof-goalTag">{p.goal.cadence}</span>
              <span className="Marketplace-Proof-goalRaised">{p.goal.raised}</span>
              <span className="Marketplace-Proof-goalTarget">{p.goal.target}</span>
            </div>
            <div className="Marketplace-bar">
              <div className="Marketplace-barFill" style={{ width: p.goal.w }} />
            </div>
            <div className="Marketplace-Proof-goalFoot">
              <span className="Marketplace-Proof-goalPct">{p.goal.pct}</span>
              <span className="Marketplace-Proof-goalNote">{p.goal.note}</span>
              {editing && (
                <span className="Marketplace-Proof-rowActions">
                  <span className="Marketplace-Proof-tileBtn" onClick={p.editGoal}>
                    {vm.m.proofEdit}
                  </span>
                  <span className="Marketplace-Proof-tileBtn" onClick={p.deleteGoal}>
                    {vm.m.proofRemove}
                  </span>
                </span>
              )}
            </div>
            {/* Only rendered when there is something to say — an organisation
                that only ever takes HKD never sees a currency caveat. */}
            {p.goal.ignored && (
              <div className="Marketplace-Proof-goalIgnored">{p.goal.ignored}</div>
            )}
          </div>
        ))}
      </div>
    );
  }

  /* --- part 1 · key metrics ---------------------------------------------- */

  renderMetricForm(vm, p) {
    return (
      <div className="Marketplace-Proof-form" data-tour="proof-metric-form">
        <div className="Marketplace-Proof-formTitle">{vm.m.proofMetricFormTitle}</div>
        <div className="Marketplace-Proof-templateLabel">{vm.m.proofTemplateLabel}</div>
        <div className="Marketplace-Proof-templates plug-hscroll">
          {p.metricTemplates.map((t) => (
            <div
              className={
                "Marketplace-Proof-template" +
                (t.active ? " Marketplace-Proof-template--on" : "")
              }
              key={t.id}
              onClick={t.onPick}
            >
              {t.label}
            </div>
          ))}
        </div>
        <div className="Marketplace-Proof-customRow">
          <span className="Marketplace-Proof-scrollHint">{vm.m.proofScrollHint}</span>
          <div
            className={
              "Marketplace-Proof-custom" +
              (p.customActive ? " Marketplace-Proof-custom--on" : "")
            }
            onClick={p.pickCustom}
          >
            {vm.m.proofCustomMetric}
          </div>
        </div>
        <div className="Marketplace-Proof-editor">
          <div className="Marketplace-Proof-field Marketplace-Proof-field--grow">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofMeasures}</div>
            <input
              className="Marketplace-Proof-input"
              type="text"
              maxLength={80}
              value={p.metricLabel}
              onChange={p.onMetricLabel}
              placeholder={vm.m.proofMetricPlaceholder}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--num">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofNumber}</div>
            <input
              className="Marketplace-Proof-input"
              type="number"
              value={p.metricValue}
              onChange={p.onMetricValue}
              placeholder={vm.m.proofNumberHint}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--format">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofFormat}</div>
            <select
              className="Marketplace-Proof-input Marketplace-Proof-select"
              value={p.metricFormat}
              onChange={p.onMetricFormat}
            >
              {p.metricFormats.map((f) => (
                <option value={f.id} key={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--period">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofPeriod}</div>
            <input
              className="Marketplace-Proof-input"
              type="text"
              maxLength={40}
              value={p.metricPeriod}
              onChange={p.onMetricPeriod}
              placeholder={vm.m.proofPeriodHint}
            />
          </div>
          {/* Highlighting, decided before the metric exists rather than only
              afterwards on the tile. A tick and not the tile's switch: inside
              a form nothing takes effect until Add is pressed, and a control
              that wrote on its own press would be the one thing here that
              did. Reopening a tile to edit brings its state back in, so this
              reads the tile as well as sets it. */}
          <div className="Marketplace-Proof-field Marketplace-Proof-field--hi">
            <div className="Marketplace-Proof-fieldLabel">
              {vm.m.proofColHighlight}
            </div>
            <div
              className="Marketplace-Proof-hiPick"
              role="checkbox"
              aria-checked={!!p.metricHighlight}
              aria-label={vm.m.proofHighlight}
              onClick={p.onMetricHighlight}
            >
              <span
                className={
                  "Marketplace-Proof-check Marketplace-Proof-check--form" +
                  (p.metricHighlight ? " Marketplace-Proof-check--on" : "")
                }
              >
                {p.metricHighlight ? "\u2713" : ""}
              </span>
              <span className="Marketplace-Proof-hiPickLabel">
                {vm.m.proofHighlightNew}
              </span>
            </div>
          </div>
          <div
            className={
              "Marketplace-btnPrimary Marketplace-Proof-addBtnInline" +
              (p.canAddMetric && !p.saving ? "" : " Marketplace-btn--inert")
            }
            onClick={p.canAddMetric && !p.saving ? p.addMetric : undefined}
          >
            {p.addMetricLabel}
          </div>
        </div>
        {/* The artboard's second route to a metric: upload a report and have
            the numbers pulled out of it. Held back (NOT_READY in
            Marketplace.js) because nothing reads the document — there is no
            endpoint that parses a report, so the file was picked, named, and
            then left where it was. The markup and its handlers stay for when
            one exists; only the drawing of it is gated. */}
        {!!p.showMetricReport && (
          <div className="Marketplace-Proof-report">
            <div className="Marketplace-Proof-reportIcon">&#8593;</div>
            <div className="Marketplace-Proof-reportMain">
              <div className="Marketplace-Proof-reportTitle">{vm.m.proofReportTitle}</div>
              <div className="Marketplace-Proof-reportBody">{vm.m.proofReportBody}</div>
              {this.state.metricReport && (
                <div className="Marketplace-Proof-fileList">
                  <div className="Marketplace-Proof-file">
                    <span className="Marketplace-Proof-fileName">
                      {this.state.metricReport.name}
                    </span>
                    <span
                      className="Marketplace-Proof-fileRemove"
                      onClick={this.removeMetricReport}
                    >
                    &times;
                    </span>
                  </div>
                </div>
              )}
              <div className="Marketplace-Proof-reportPending">
                {vm.m.proofReportPending}
              </div>
            </div>
            <div
              className="Marketplace-Proof-reportBtn"
              onClick={this.pick(this.metricReportInput)}
            >
              {vm.m.proofReportBtn}
            </div>
            <input
              className="Marketplace-Proof-fileInput"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              ref={this.metricReportInput}
              onChange={this.onMetricReportPicked}
            />
          </div>
        )}
        {this.renderFormError(p, ["proofErrors.label", "proofErrors.value", "proofErrors.save", "proofErrors.limit"])}
      </div>
    );
  }

  /**
   * The artboard's EDIT KEY METRICS panel: every tile as a row, edited
   * together and written in one press.
   *
   * Highlighting is a tick here rather than the switch it was on the tile --
   * inside a table of rows being changed together, a control that took effect
   * on its own would be the one thing that did.
   */
  renderMetricTable(vm, p) {
    return (
      <div className="Marketplace-Proof-form">
        <div className="Marketplace-Proof-formTitle">{vm.m.proofMetricTableTitle}</div>
        <div className="Marketplace-Proof-tableHead">
          <span className="Marketplace-Proof-colHi">{vm.m.proofColHighlight}</span>
          <span className="Marketplace-Proof-colNum">{vm.m.proofColNumber}</span>
          <span className="Marketplace-Proof-colGrow">{vm.m.proofColMeans}</span>
          <span className="Marketplace-Proof-colGap" />
        </div>
        <div className="Marketplace-Proof-tableRows">
          {p.metricRows.map((r) => (
            <div className="Marketplace-Proof-tableRow" key={r.key}>
              <span
                className={
                  "Marketplace-Proof-check" +
                  (r.highlighted ? " Marketplace-Proof-check--on" : "")
                }
                role="checkbox"
                aria-checked={r.highlighted}
                aria-label={vm.m.proofHighlight}
                onClick={r.onHighlight}
              >
                {r.highlighted ? "\u2713" : ""}
              </span>
              <input
                className="Marketplace-Proof-input Marketplace-Proof-rowNum"
                type="text"
                value={r.value}
                onChange={r.onValue}
                placeholder={vm.m.proofNumberHint}
              />
              <input
                className="Marketplace-Proof-input Marketplace-Proof-rowGrow"
                type="text"
                maxLength={80}
                value={r.label}
                onChange={r.onLabel}
                placeholder={vm.m.proofColMeans}
              />
              <span className="Marketplace-Proof-rowRemove" onClick={r.onRemove}>
                &times;
              </span>
            </div>
          ))}
        </div>
        {this.renderFormError(p, ["proofErrors.save", "proofErrors.limit"])}
        <div className="Marketplace-Proof-tableFoot">
          <div className="Marketplace-Proof-addRow" onClick={p.addMetricRow}>
            {vm.m.proofAddAnotherMetric}
          </div>
          <span className="Marketplace-Proof-tableHint">{vm.m.proofHighlightHint}</span>
          <div
            className="Marketplace-Proof-tableCancel"
            onClick={p.toggleMetricsEdit}
          >
            {vm.m.proofCancel}
          </div>
          <div
            className={
              "Marketplace-Proof-tableSave" + (p.saving ? " Marketplace-btn--inert" : "")
            }
            onClick={p.saving ? undefined : p.saveMetricTable}
          >
            {vm.m.proofSaveChanges}
          </div>
        </div>
      </div>
    );
  }

  renderMetrics(vm, p, isPublic) {
    const editing = !isPublic && p.metricsEditing;
    return (
      <div className="Marketplace-card">
        {this.renderHead(
          vm.m.proofPart1,
          "",
          p.metricAddLabel,
          p.toggleMetricAdd,
          isPublic,
          p.metricOpen,
          {
            anchor: "proof-metric-add",
            tip: isPublic ? null : this.tip(vm, "metricPanel"),
          }
        )}
        {!isPublic && p.metricOpen && this.renderMetricForm(vm, p)}
        {editing && this.renderMetricTable(vm, p)}
        {this.renderBody(
          vm,
          p.metricsState,
          p.part1Empty,
          p.bigMetrics.length === 0,
          () => (
            <div>
              {this.renderSub(
                p.part1Head,
                p.metricsEditing ? vm.m.proofDone : vm.m.proofEditMetrics,
                p.toggleMetricsEdit,
                isPublic
              )}
              {/* The period is said once, in the subhead above, rather than
                repeated under every tile as it used to be. */}
              <div className="Marketplace-Proof-bigMetrics">
                {p.bigMetrics.map((b) => (
                  <div
                    className={
                      "Marketplace-Proof-bigMetric" +
                      (b.lead ? " Marketplace-Proof-bigMetric--lead" : "") +
                      // "Just added" is a passing acknowledgement and
                      // highlighting is a standing choice, so the two never
                      // compete: a leading tile keeps the lead treatment
                      // however recently it was published.
                      (b.fresh && !b.lead ? " Marketplace-Proof-bigMetric--fresh" : "")
                    }
                    key={b.id}
                  >
                    <div className="Marketplace-Proof-bigValue">{b.value}</div>
                    <div className="Marketplace-Proof-bigLabel">{b.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        )}
      </div>
    );
  }

  /* --- part 2 · what a giving buys ---------------------------------------- */

  renderBuyForm(vm, p) {
    return (
      <div className="Marketplace-Proof-form">
        <div className="Marketplace-Proof-formTitle">{vm.m.proofBuyFormTitle}</div>
        <div className="Marketplace-Proof-templateLabel">{vm.m.proofBuyExamples}</div>
        <div className="Marketplace-Proof-templates plug-hscroll">
          {p.buyTemplates.map((t) => (
            <div
              className={
                "Marketplace-Proof-template" +
                (t.active ? " Marketplace-Proof-template--on" : "")
              }
              key={t.id}
              onClick={t.onPick}
            >
              {t.label}
            </div>
          ))}
        </div>
        <div className="Marketplace-Proof-customRow">
          <span className="Marketplace-Proof-scrollHint">{vm.m.proofBuyScrollHint}</span>
          <div
            className={
              "Marketplace-Proof-custom" +
              (p.buyCustomActive ? " Marketplace-Proof-custom--on" : "")
            }
            onClick={p.pickBuyCustom}
          >
            {vm.m.proofCustomBuy}
          </div>
        </div>
        <div className="Marketplace-Proof-fieldLabel Marketplace-Proof-blockLabel">
          {vm.m.proofPickIcon}
        </div>
        {/* An icon and an emoji are alternatives, not a pair: picking either
            clears the other, so a row never carries two answers for one slot. */}
        <div className="Marketplace-Proof-iconPicker">
          {p.buyIcons.map((icon) => (
            <div
              className={
                "Marketplace-Proof-iconBtn" +
                (icon.active ? " Marketplace-Proof-iconBtn--on" : "")
              }
              key={icon.id}
              onClick={icon.onPick}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke={icon.active ? "#175b89" : "#606266"}
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={icon.path} />
              </svg>
            </div>
          ))}
          <div className="Marketplace-Proof-iconDivider" />
          <input
            className="Marketplace-Proof-emojiInput"
            type="text"
            value={p.buyEmoji}
            onChange={p.onBuyEmoji}
            placeholder="🏠"
          />
          <span className="Marketplace-Proof-scrollHint">{vm.m.proofEmojiHint}</span>
        </div>
        <div className="Marketplace-Proof-editor">
          <div className="Marketplace-Proof-field Marketplace-Proof-field--amount">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofBuyAmount}</div>
            <input
              className="Marketplace-Proof-input"
              type="number"
              value={p.buyAmount}
              onChange={p.onBuyAmount}
              placeholder={vm.m.proofBuyAmountHint}
            />
          </div>
          <div className="Marketplace-Proof-field Marketplace-Proof-field--wide">
            <div className="Marketplace-Proof-fieldLabel">{vm.m.proofBuyBuys}</div>
            <input
              className="Marketplace-Proof-input"
              type="text"
              maxLength={120}
              value={p.buyLabel}
              onChange={p.onBuyLabel}
              placeholder={vm.m.proofBuyLabelHint}
            />
          </div>
          <div
            className={
              "Marketplace-btnPrimary Marketplace-Proof-addBtnInline" +
              (p.canAddBuy && !p.saving ? "" : " Marketplace-btn--inert")
            }
            onClick={p.canAddBuy && !p.saving ? p.addBuy : undefined}
          >
            {p.addBuyLabel}
          </div>
        </div>
        {this.renderFormError(p, ["proofErrors.buyLabel", "proofErrors.buyAmount", "proofErrors.save", "proofErrors.limit"])}
      </div>
    );
  }

  /**
   * The artboard's EDIT WHAT A GIVING BUYS panel. Each row carries the icon
   * picker for its own line, opened under the row it belongs to.
   */
  renderBuyTable(vm, p) {
    return (
      <div className="Marketplace-Proof-form">
        <div className="Marketplace-Proof-formTitle">{vm.m.proofBuyTableTitle}</div>
        <div className="Marketplace-Proof-tableRows">
          {p.buyRows.map((r) => (
            <div key={r.key}>
              <div className="Marketplace-Proof-tableRow">
                <div
                  className="Marketplace-Proof-rowIcon"
                  title={vm.m.proofChooseIcon}
                  onClick={r.togglePicker}
                >
                  {r.emoji ? (
                    <span className="Marketplace-Proof-buyEmoji">{r.emoji}</span>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      width="19"
                      height="19"
                      fill="none"
                      stroke="#175b89"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={r.iconPath} />
                    </svg>
                  )}
                </div>
                <input
                  className="Marketplace-Proof-emojiInput Marketplace-Proof-rowEmoji"
                  type="text"
                  value={r.emoji}
                  onChange={r.onEmoji}
                  placeholder="&#127968;"
                />
                <input
                  className="Marketplace-Proof-input Marketplace-Proof-rowAmount"
                  type="text"
                  value={r.amount}
                  onChange={r.onAmount}
                  placeholder={vm.m.proofBuyAmountHint}
                />
                <input
                  className="Marketplace-Proof-input Marketplace-Proof-rowGrow"
                  type="text"
                  maxLength={120}
                  value={r.label}
                  onChange={r.onLabel}
                  placeholder={vm.m.proofBuyLabelHint}
                />
                <span className="Marketplace-Proof-rowRemove" onClick={r.onRemove}>
                  &times;
                </span>
              </div>
              {r.pickerOpen && (
                <div className="Marketplace-Proof-rowPicker">
                  <span className="Marketplace-Proof-rowPickerLabel">
                    {vm.m.proofPickIcon}
                  </span>
                  {r.icons.map((icon) => (
                    <div
                      className={
                        "Marketplace-Proof-rowIconBtn" +
                        (icon.active ? " Marketplace-Proof-rowIconBtn--on" : "")
                      }
                      key={icon.id}
                      onClick={icon.onPick}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        fill="none"
                        stroke={icon.active ? "#175b89" : "#606266"}
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d={icon.path} />
                      </svg>
                    </div>
                  ))}
                  <span
                    className="Marketplace-Proof-rowPickerClose"
                    onClick={r.togglePicker}
                  >
                    {vm.m.proofClose}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
        {this.renderFormError(p, ["proofErrors.save"])}
        <div className="Marketplace-Proof-tableFoot">
          <div className="Marketplace-Proof-addRow" onClick={p.addBuyRow}>
            {vm.m.proofAddAnotherLine}
          </div>
          <div className="Marketplace-Proof-tableCancel" onClick={p.toggleBuysEdit}>
            {vm.m.proofCancel}
          </div>
          <div
            className={
              "Marketplace-Proof-tableSave" + (p.saving ? " Marketplace-btn--inert" : "")
            }
            onClick={p.saving ? undefined : p.saveBuyTable}
          >
            {vm.m.proofSaveChanges}
          </div>
        </div>
      </div>
    );
  }

  renderBuys(vm, p, isPublic) {
    const editing = !isPublic && p.buysEditing;
    return (
      <div className="Marketplace-card">
        {this.renderHead(
          vm.m.proofPart2,
          "",
          p.buyAddLabel,
          p.toggleBuyAdd,
          isPublic,
          p.buyOpen,
          {
            anchor: "proof-buy-add",
            tip: isPublic ? null : this.tip(vm, "buys"),
          }
        )}
        {!isPublic && p.buyOpen && this.renderBuyForm(vm, p)}
        {editing && this.renderBuyTable(vm, p)}
        {this.renderBody(vm, p.buysState, p.buysEmpty, p.buys.length === 0, () => (
          <div>
            {this.renderSub(
              p.part2Head,
              p.buysEditing ? vm.m.proofDone : vm.m.proofEditBuys,
              p.toggleBuysEdit,
              isPublic
            )}
            <div className="Marketplace-Proof-buys">
              {p.buys.map((b) => (
                <div
                  className={
                    "Marketplace-Proof-buy" +
                    (b.fresh ? " Marketplace-Proof-buy--fresh" : "")
                  }
                  key={b.id}
                >
                  <div className="Marketplace-Proof-buyIcon">
                    {b.emoji ? (
                      <span className="Marketplace-Proof-buyEmoji">{b.emoji}</span>
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        fill="none"
                        stroke="#175b89"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d={b.iconPath} />
                      </svg>
                    )}
                  </div>
                  <div className="Marketplace-Proof-buyAmount">{b.amount}</div>
                  <div className="Marketplace-Proof-buyDivider" />
                  <div className="Marketplace-Proof-buyLabel">{b.label}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  /* --- part 3 · what's new ------------------------------------------------ */

  renderPostForm(vm, p, inline, u) {
    const attachHandlers = {
      photo: this.pick(this.photoInput),
      file: this.pick(this.reportInput),
    };
    return (
      <div
        className={
          "Marketplace-Proof-form" +
          (inline ? " Marketplace-Proof-form--inline" : "")
        }
        ref={inline ? undefined : this.postForm}
      >
        <div className="Marketplace-Proof-formTitle">
          {inline ? vm.m.proofPostEditTitle : vm.m.proofPostFormTitle}
        </div>
        <div className="Marketplace-Proof-composer">
          <input
            className="Marketplace-Proof-titleInput"
            type="text"
            maxLength={140}
            value={p.postTitle}
            onChange={p.onPostTitle}
            placeholder={vm.m.proofPostTitlePlaceholder}
          />
          <textarea
            className="Marketplace-Proof-textarea"
            maxLength={5000}
            value={p.postBody}
            onChange={p.onPostBody}
            placeholder={vm.m.proofPostPlaceholder}
          />
          {(this.state.photos.length > 0 || this.state.report) && (
            <div className="Marketplace-Proof-fileList">
              {this.state.photos.map((file, index) => (
                <div className="Marketplace-Proof-file" key={file.name + index}>
                  <span className="Marketplace-Proof-fileName">{file.name}</span>
                  <span
                    className="Marketplace-Proof-fileRemove"
                    onClick={this.removePhoto(index)}
                  >
                    ×
                  </span>
                </div>
              ))}
              {this.state.report && (
                <div className="Marketplace-Proof-file">
                  <span className="Marketplace-Proof-fileName">
                    {this.state.report.name}
                  </span>
                  <span
                    className="Marketplace-Proof-fileRemove"
                    onClick={this.removeReport}
                  >
                    ×
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="Marketplace-Proof-composerBar">
            {p.attachments.map((a) => (
              <div
                className="Marketplace-Proof-attach"
                key={a.id}
                onClick={a.onClick || attachHandlers[a.id]}
              >
                + {a.label}
              </div>
            ))}
            <div
              className={
                "Marketplace-btnPrimary Marketplace-Proof-postBtn" +
                (p.canPost && !p.saving ? "" : " Marketplace-btn--inert")
              }
              onClick={p.canPost && !p.saving ? this.submitPost : undefined}
            >
              {p.postBtnLabel}
            </div>
          </div>
          <input
            className="Marketplace-Proof-fileInput"
            type="file"
            accept="image/*"
            multiple
            ref={this.photoInput}
            onChange={this.onPhotosPicked}
          />
          <input
            className="Marketplace-Proof-fileInput"
            type="file"
            accept="application/pdf"
            ref={this.reportInput}
            onChange={this.onReportPicked}
          />
        </div>
        {p.ytOpen && this.renderYt(vm, p)}
        {this.renderFormError(p, ["proofErrors.title", "proofErrors.body", "proofErrors.video", "proofErrors.save", "proofErrors.noChange"])}
        {inline ? (
          <div className="Marketplace-Proof-editFoot">
            <div
              className="Marketplace-Proof-editCancel"
              onClick={p.cancelPostEdit}
            >
              {vm.m.proofCancel}
            </div>
            {/* The artboard's "Save as draft" and "Publish" are one control:
                a draft publishes and a published post goes back to a draft. */}
            <div
              className={
                "Marketplace-Proof-editDraft" +
                (u.busy ? " Marketplace-btn--inert" : "")
              }
              onClick={u.busy ? undefined : u.onToggle}
            >
              {u.toggleLabel}
            </div>
            <div
              className={
                "Marketplace-Proof-editDelete" +
                (u.busy ? " Marketplace-btn--inert" : "")
              }
              onClick={u.busy ? undefined : u.onDelete}
            >
              {vm.m.proofDeletePost}
            </div>
          </div>
        ) : (
          <div className="Marketplace-Proof-formNote">{p.postNote}</div>
        )}
      </div>
    );
  }

  /**
   * The artboard's YouTube panel: the field, and once the link resolves to an
   * id, the thumbnail that becomes the player.
   *
   * The video's title and length are not shown. Both need a call to YouTube's
   * Data API, which this app has no key for, and a title guessed from the id
   * would be a caption the organisation never wrote.
   */
  renderYt(vm, p) {
    const id = p.postVideoId;
    return (
      <div className="Marketplace-Proof-yt">
        <div className="Marketplace-Proof-ytBar">
          <input
            className="Marketplace-Proof-input"
            type="text"
            value={p.postVideo}
            onChange={p.onPostVideo}
            placeholder={vm.m.proofYtHint}
          />
          <div className="Marketplace-Proof-ytClear" onClick={p.clearPostVideo}>
            {vm.m.proofYtClear}
          </div>
        </div>
        {!id && <div className="Marketplace-Proof-ytEmpty">{vm.m.proofYtEmpty}</div>}
        {id && (
          <div className="Marketplace-Proof-ytPreview">
            {this.state.ytPlaying ? (
              <iframe
                className="Marketplace-Proof-ytFrame"
                title={vm.m.proofYtFrame}
                src={"https://www.youtube-nocookie.com/embed/" + id + "?autoplay=1"}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="Marketplace-Proof-ytThumb" onClick={this.playYt}>
                <img
                  className="Marketplace-Proof-ytThumbImg"
                  src={"https://img.youtube.com/vi/" + id + "/hqdefault.jpg"}
                  alt=""
                />
                <span className="Marketplace-Proof-ytPlay">&#9654;</span>
              </div>
            )}
            <div className="Marketplace-Proof-ytSide">
              <div className="Marketplace-Proof-ytNote">{vm.m.proofYtNote}</div>
              <div className="Marketplace-Proof-ytActions">
                <div
                  className="Marketplace-Proof-ytPlayBtn"
                  onClick={this.state.ytPlaying ? this.stopYt : this.playYt}
                >
                  {this.state.ytPlaying ? vm.m.proofYtReset : vm.m.proofYtPlay}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * The artboard's profile card: the cover, the logo tile over its lower
   * left, the organisation's name, and the introduction.
   *
   * The logo opens CheckPlus rather than a picker. It is part of the
   * organisation record, which that system owns -- 'Our profile' sends people
   * to the same place for the same reason.
   */
  renderProfileCard(vm, p) {
    const id = p.identity;
    const editing = this.state.introEditing;
    return (
      <div className="Marketplace-Proof-profileCard">
        <div className="Marketplace-Proof-cover Marketplace-Proof-cover--inCard">
          {this.renderCoverInto(vm, p.cover, "Marketplace-Proof-coverImg", p.coverPosition)}
          <div
            className="Marketplace-Proof-coverEdit"
            onClick={p.openCoverModal}
            data-tour="proof-cover-edit"
          >
            {vm.m.proofCoverEdit}
          </div>
        </div>
        <div className="Marketplace-Proof-profileBody">
          <div className="Marketplace-Proof-profileTop">
            <div
              className="Marketplace-Proof-profileMono"
              style={{ background: id.monoBg, color: id.monoFg }}
            >
              {id.logo ? (
                <img
                  className="Marketplace-Proof-publicMonoImg"
                  src={id.logo}
                  alt=""
                  onLoad={this.logoSettled(id.logo)}
                  onError={this.logoSettled(id.logo)}
                />
              ) : (
                id.mono
              )}
              {this.logoBusy(id) && (
                <span
                  className="Marketplace-Proof-logoBusy"
                  role="status"
                  aria-label={id.logoBusyLabel}
                >
                  <span className="Marketplace-Proof-logoSpinner" />
                </span>
              )}
              {/* The picker itself, in place of the link out to CheckPlus
                  this used to be. The warning under the card is what makes
                  that safe to offer here — see id.logoShared. */}
              <label
                className={
                  "Marketplace-Proof-profileLogoBtn" +
                  (id.logoUploading ? " Marketplace-Proof-profileLogoBtn--busy" : "")
                }
                title={vm.m.proofEditLogo}
              >
                ✎
                <input
                  type="file"
                  className="Marketplace-Proof-fileInput"
                  accept="image/jpeg,image/png"
                  onChange={id.onPickLogo}
                  disabled={id.logoUploading}
                  aria-label={vm.m.proofEditLogo}
                />
              </label>
            </div>
            <div className="Marketplace-Proof-profileWho">
              <div className="Marketplace-Proof-profileName">{id.name}</div>
              {id.meta && (
                <div className="Marketplace-Proof-profileMeta">{id.meta}</div>
              )}
              {/* This page belongs to a nonprofit, and a nonprofit's logo is
                  its CheckPlus organisation record's — so changing it here
                  changes it there. Said beside the picker rather than after
                  the upload, which would be telling them once it is too late
                  to decide otherwise. */}
              {/* The brand colour, the logo tile's background. The tile beside
                  it follows as the picker moves; see onPickColor (CP-370). */}
              <label className="Marketplace-Proof-profileColor">
                <span
                  className="Marketplace-Proof-profileColorSwatch"
                  style={{ background: id.colorValue }}
                />
                {id.colorLabel}
                <input
                  type="color"
                  className="Marketplace-Proof-fileInput"
                  value={id.colorValue}
                  onChange={id.onPickColor}
                  aria-label={id.colorLabel}
                />
              </label>
              {/* Covers the colour as well as the logo now: both are fields on
                  the CheckPlus organisation record. */}
              <div className="Marketplace-Proof-profileLogoShared">
                {id.logoShared}
              </div>
              {!!id.logoState && (
                <div
                  className={
                    "Marketplace-Proof-profileLogoState" +
                    (id.logoBad ? " Marketplace-Proof-profileLogoState--bad" : "")
                  }
                >
                  {id.logoState}
                </div>
              )}
              {!!id.colorState && (
                <div
                  className={
                    "Marketplace-Proof-profileLogoState" +
                    (id.colorBad ? " Marketplace-Proof-profileLogoState--bad" : "")
                  }
                >
                  {id.colorState}
                </div>
              )}
            </div>
          </div>
          <div className="Marketplace-Proof-profileIntroRow">
            <div className="Marketplace-Proof-profileIntro">
              {editing ? (
                <div>
                  <textarea
                    className="Marketplace-Proof-introText"
                    maxLength={280}
                    value={id.intro.value}
                    placeholder={id.intro.placeholder}
                    onChange={id.intro.onText}
                  />
                  <div className="Marketplace-Proof-introActions">
                    <div className="Marketplace-Proof-introSave" onClick={this.saveIntro}>
                      {vm.m.proofIntroSave}
                    </div>
                    <div
                      className="Marketplace-Proof-introCancel"
                      onClick={this.cancelIntro}
                    >
                      {vm.m.proofCancel}
                    </div>
                    <span className="Marketplace-Proof-introNote">
                      {vm.m.proofIntroNote}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="Marketplace-Proof-introRead">
                  {id.introSaved || vm.m.proofIntroEmpty}
                </div>
              )}
            </div>
            {!editing && (
              <div
                className="Marketplace-Proof-introEditBtn"
                onClick={this.startIntroEdit}
                data-tour="proof-intro-edit"
              >
                {vm.m.proofIntroEdit}
              </div>
            )}
            {/* Outside the guard above on purpose: the guidance is worth as
                much while the sentence is being written as before it. */}
            <InfoTip tip={this.tip(vm, "intro")} place="left" />
          </div>
        </div>
      </div>
    );
  }

  /**
   * The artboard's cover modal. The picker used to fire straight off the
   * button and upload whatever was chosen; here the image is previewed first
   * and only "Save image" writes it.
   */
  renderCoverModal(vm, p) {
    const shown = this.state.coverDraftUrl || p.cover;
    const filled = !!shown && this.state.coverError !== shown;
    const frame = this.coverFrameOf(p);
    const dirty = this.coverDirty(p);
    // A save or a removal in flight: both run under p.saving, which holds
    // until the page has been re-read.
    const busy = !!p.saving;
    // Only describe the stored cover while the stored cover is what is shown.
    const shownName = this.state.coverDraftUrl
      ? this.state.coverDraft && this.state.coverDraft.name
      : p.coverName;
    return (
      <div className="Marketplace-Proof-modalWrap">
        <div className="Marketplace-Proof-modalScrim" onClick={this.closeCoverModal} />
        <div className="Marketplace-Proof-modal">
          <div className="Marketplace-Proof-modalHead">
            <div className="Marketplace-Proof-modalTitle">{vm.m.proofCoverModalTitle}</div>
            <div className="Marketplace-Proof-modalClose" onClick={this.closeCoverModal}>
              &times;
            </div>
          </div>
          <div className="Marketplace-Proof-modalBody">
            <div className="Marketplace-Proof-modalBlurb">
              {vm.m.proofCoverModalBlurb}
            </div>
            {/* Without this the cover error had nowhere to render at all: the
                other six call sites are forms, and this modal is the only way
                to reach a cover write. */}
            {this.renderFormError(p, ["proofErrors.cover", "proofErrors.coverType", "proofErrors.coverSize", "proofErrors.save"])}
            {/* One place for the image and for choosing it (CP-373): the
                preview is the drop target and the picker, rather than a
                preview with a separate drop box under it. Empty, it says how;
                filled, it keeps a "Choose file" pill so it is plain the image
                can be replaced. The draft is a blob: URL and always loads; the
                stored cover is the one that can fail, and the modal is where
                somebody has come to do something about it. */}
            {/* Empty, the area is a button that opens the picker. Holding an
                image it is the framing editor instead (CP-374): drag or use
                the arrow keys to position, and the pill chooses a new file.
                A dropped file is taken either way. */}
            <div
              ref={this.coverZone}
              className={
                "Marketplace-Proof-coverZone" +
                (filled ? " Marketplace-Proof-coverZone--filled" : "") +
                (this.state.coverOver ? " Marketplace-Proof-coverZone--over" : "") +
                (busy ? " Marketplace-Proof-coverZone--busy" : "")
              }
              role={filled ? "group" : "button"}
              tabIndex={busy ? -1 : 0}
              aria-label={filled ? vm.m.proofCoverDragHint : vm.m.proofCoverZoneLabel}
              aria-disabled={busy || undefined}
              onClick={busy || filled ? undefined : this.pick(this.coverInput)}
              onKeyDown={
                busy ? undefined : filled ? this.nudgeCover : this.pickOnKey(this.coverInput)
              }
              onPointerDown={busy || !filled ? undefined : this.startCoverDrag}
              onPointerMove={busy || !filled ? undefined : this.moveCoverDrag}
              onPointerUp={this.endCoverDrag}
              onPointerCancel={this.endCoverDrag}
              onDragOver={this.overCover}
              onDragLeave={this.leaveCover}
              onDrop={busy ? this.ignoreDrop : this.dropCover}
            >
              {filled && (
                <img
                  className="Marketplace-Proof-coverImg"
                  src={shown}
                  alt=""
                  draggable={false}
                  style={coverFrameStyle(frame)}
                  onLoad={this.coverNaturalSize}
                  onError={this.coverBroke(shown)}
                />
              )}
              {filled ? (
                <button
                  type="button"
                  className="Marketplace-Proof-dropBtn Marketplace-Proof-coverZoneReplace"
                  onClick={this.pickFromPill}
                  onPointerDown={this.stopPointer}
                  disabled={busy}
                >
                  {vm.m.proofReportBtn}
                </button>
              ) : (
                <div className="Marketplace-Proof-coverZoneEmpty">
                  <span className="Marketplace-Proof-coverHint">
                    {/* `!!shown` guards the comparison the way renderCoverInto
                        does. Without it the two empty strings matched — a page
                        with no cover has `shown === ""` and coverError starts
                        "" — and every organisation that had never set one was
                        told the cover it did not have would not load. */}
                    {!!shown && this.state.coverError === shown
                      ? vm.m.proofCoverBroken
                      : vm.m.proofCoverModalNote}
                  </span>
                  <div className="Marketplace-Proof-dropTitle">{vm.m.proofDropTitle}</div>
                  <span className="Marketplace-Proof-dropBtn">{vm.m.proofReportBtn}</span>
                </div>
              )}
              {busy && (
                <span
                  className="Marketplace-Proof-coverZoneBusy"
                  role="status"
                  aria-label={p.coverRemoving ? vm.m.proofCoverRemoving : vm.m.proofCoverSaving}
                >
                  <span className="Marketplace-Proof-logoSpinner" />
                </span>
              )}
            </div>
            {filled && (
              <div className="Marketplace-Proof-coverFrameRow">
                <label className="Marketplace-Proof-coverZoom">
                  {vm.m.proofCoverZoom}
                  <input
                    type="range"
                    min="1"
                    max={String(COVER_ZOOM_MAX)}
                    step="0.01"
                    value={frame.zoom}
                    onChange={this.onCoverZoom}
                    disabled={busy}
                  />
                </label>
                <button
                  type="button"
                  className="Marketplace-Proof-coverReset"
                  onClick={this.resetCoverFrame}
                  disabled={busy || sameCoverPosition(frame, COVER_POSITION_DEFAULT)}
                >
                  {vm.m.proofCoverReset}
                </button>
              </div>
            )}
            <div className="Marketplace-Proof-dropNote">
              {filled ? vm.m.proofCoverDragHint + " " : ""}
              {vm.m.proofCoverSpec}
            </div>
            {/* Names whichever image is actually on screen -- the stored one,
                or the one just picked. */}
            {shownName && (
              <div className="Marketplace-Proof-dropNote">
                {fill(vm.m.proofCoverFileNote, { name: shownName })}
              </div>
            )}
          </div>
          <div className="Marketplace-Proof-modalFoot">
            {/* Only offered when there is something to take down. */}
            {p.cover ? (
              <span
                className={
                  "Marketplace-Proof-modalRemove" +
                  (busy ? " Marketplace-Proof-modalRemove--busy" : "")
                }
                onClick={busy ? undefined : p.removeCover}
                aria-disabled={busy || undefined}
              >
                {p.coverRemoving ? vm.m.proofCoverRemoving : vm.m.proofCoverRemove}
              </span>
            ) : (
              <span className="Marketplace-Proof-modalFootGap" />
            )}
            <div className="Marketplace-Proof-modalCancel" onClick={this.closeCoverModal}>
              {vm.m.proofCancel}
            </div>
            <div
              className={
                "Marketplace-Proof-modalSave" +
                (dirty && !p.saving ? "" : " Marketplace-btn--inert")
              }
              onClick={dirty && !p.saving ? this.saveCover : undefined}
            >
              {vm.m.proofCoverSave}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * A post as a supporter reads it, which is a different thing from the row
   * the organisation edits.
   *
   * The artboard gives a public post its own card: the copy truncated with
   * `See more`, then either a video poster that plays in place or the photo
   * mosaic, and the whole thing opening out into a full-width frame with a
   * scrolling strip of every photo under it. The organisation's own list
   * stays the compact row below — it is an editing surface, and a gallery in
   * the middle of the edit controls is not what the artboard draws.
   */
  renderPublicUpdate(vm, u) {
    const media = u.media;
    return (
      <div
        className={
          "Marketplace-Proof-post" +
          (media.open ? " Marketplace-Proof-post--open" : "")
        }
        key={u.id}
      >
        <div
          className="Marketplace-Proof-postHead"
          {...activate(media.toggle)}
        >
          <span className="Marketplace-Proof-postTitle">{u.title}</span>
          <span className="Marketplace-Proof-postMeta">{u.meta}</span>
        </div>

        <div className="Marketplace-Proof-postCopy" onClick={media.toggle}>
          {media.body}{" "}
          {media.truncated && (
            <span className="Marketplace-Proof-postMore">
              {media.moreLabel}
            </span>
          )}
        </div>

        {media.isVideo && (
          <div
            className={
              "Marketplace-Proof-video" +
              (media.open ? " Marketplace-Proof-video--open" : "")
            }
          >
            {media.videoPlaying ? (
              <iframe
                className="Marketplace-Proof-videoFrame"
                src={media.videoEmbed}
                title={u.title}
                frameBorder="0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div
                className="Marketplace-Proof-videoPoster"
                {...activate(media.playVideo)}
              >
                {/* YouTube's own thumbnail for the id, so a poster costs no
                    upload and is never a second copy to keep in step. */}
                <img
                  className="Marketplace-Proof-videoImg"
                  src={media.videoPoster}
                  alt=""
                />
                <span className="Marketplace-Proof-videoPlay">▶</span>
                <a
                  className="Marketplace-Proof-videoOut"
                  href={media.videoWatch}
                  target="_blank"
                  rel="noopener noreferrer"
                  // The poster around this is `activate`d, and its Enter/Space
                  // handler calls preventDefault before playing — so a keydown
                  // allowed to bubble cancels the browser's own link
                  // activation and plays the embed instead. Without this a
                  // keyboard user can never reach YouTube from the card.
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  {media.youtubeLabel}
                </a>
                <span className="Marketplace-Proof-videoHint">
                  {media.playHint}
                </span>
              </div>
            )}
          </div>
        )}

        {media.hasPhotos &&
          (media.open
            ? this.renderGallery(vm, media)
            : this.renderMosaic(vm, media))}

        {!!media.report && (
          <a
            className="Marketplace-Proof-postReport"
            href={media.report.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            {media.reportLabel}
            <span className="Marketplace-Proof-postReportName">
              {media.report.name}
            </span>
          </a>
        )}
      </div>
    );
  }

  // Collapsed: the artboard's mosaic — one large frame beside four small
  // ones, the last of them carrying "+N" when there are more behind it.
  //
  // The count goes on as a modifier because the grid depends on it. The
  // artboard draws the five-photo case, and every layout was using it: two
  // photos came out as one large frame, one small one and half a grid of
  // white space. One class per count from one to five, and the CSS lays each
  // out on its own terms -- halves for two, thirds for three, a square for
  // four, and the artboard's own for five and up.
  renderMosaic(vm, media) {
    return (
      <div
        className={
          "Marketplace-Proof-mosaic Marketplace-Proof-mosaic--n" + media.mosaicSize
        }
        {...activate(media.toggle)}
      >
        <img
          className="Marketplace-Proof-mosaicLead"
          src={media.lead.url}
          alt={media.lead.filename || ""}
        />
        {media.tiles.map((tile) => (
          <div className="Marketplace-Proof-mosaicTile" key={tile.id}>
            <img
              className="Marketplace-Proof-mosaicImg"
              src={tile.url}
              alt={tile.alt}
            />
            {!!tile.moreLabel && (
              <span className="Marketplace-Proof-mosaicMore">
                {tile.moreLabel}
              </span>
            )}
          </div>
        ))}
        <span className="Marketplace-Proof-mosaicNote">
          {media.galleryNote}
        </span>
      </div>
    );
  }

  // Open: one large frame with arrows and a counter, and every photo in a
  // strip under it.
  renderGallery(vm, media) {
    return (
      <div className="Marketplace-Proof-gallery">
        <div className="Marketplace-Proof-shot">
          <img
            className="Marketplace-Proof-shotImg"
            src={media.shot.url}
            alt={media.shot.filename || ""}
          />
          {media.hasManyPhotos && (
            <React.Fragment>
              <span
                className="Marketplace-Proof-shotPrev"
                {...activate(media.prevShot)}
              >
                ‹
              </span>
              <span
                className="Marketplace-Proof-shotNext"
                {...activate(media.nextShot)}
              >
                ›
              </span>
              <span className="Marketplace-Proof-shotCount">
                {media.shotCounter}
              </span>
            </React.Fragment>
          )}
        </div>
        {media.hasManyPhotos && (
          <React.Fragment>
            <div className="Marketplace-Proof-strip plug-hscroll">
              {media.strip.map((shot) => (
                <img
                  className={
                    "Marketplace-Proof-stripShot" +
                    (shot.on ? " Marketplace-Proof-stripShot--on" : "")
                  }
                  src={shot.url}
                  alt={shot.alt}
                  {...activate(shot.pick)}
                  key={shot.id}
                />
              ))}
            </div>
            <div className="Marketplace-Proof-stripNote">
              {media.stripNote}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }

  renderUpdate(vm, p, u, isPublic) {
    if (isPublic) {
      return this.renderPublicUpdate(vm, u);
    }
    const editing = !isPublic && p.editingPostId === u.id;
    return (
      <div className="Marketplace-Proof-row" key={u.id}>
        <div className="Marketplace-Proof-item">
          {u.thumb ? (
            <img className="Marketplace-Proof-thumb" src={u.thumb} alt="" />
          ) : (
            <div className="Marketplace-Proof-slot">{u.slot}</div>
          )}
          <div className="Marketplace-Proof-itemBody">
            <div className="Marketplace-Proof-itemHead">
              <span className="Marketplace-Proof-itemTitle">{u.title}</span>
              {!isPublic && (
                <span
                  className="Marketplace-Proof-status"
                  style={{ background: u.statusBg, color: u.statusFg }}
                >
                  {u.statusLabel}
                </span>
              )}
            </div>
            <div className="Marketplace-Proof-itemCopy">{u.body}</div>
            {/* The organisation's own row, and nothing more: a thumbnail,
              the words, and what is attached named in the meta line. The
              gallery and the video player are the supporter's view of the
              same post — see renderPublicUpdate — because this list is an
              editing surface and the artboard keeps them off it. */}
            <div className="Marketplace-Proof-itemMeta">
              <span className="Marketplace-Proof-metaText">{u.meta}</span>
              {!isPublic && (
                <span className="Marketplace-Proof-itemActions">
                  <span
                    className={
                      "Marketplace-Proof-editPost" +
                    (u.busy ? " Marketplace-btn--inert" : "")
                    }
                    onClick={u.busy ? undefined : u.onEdit}
                  >
                    {vm.m.proofEditPost}
                  </span>
                  <span
                    className={
                      "Marketplace-Proof-tileBtn" +
                    (u.busy ? " Marketplace-btn--inert" : "")
                    }
                    onClick={u.busy ? undefined : u.onDelete}
                  >
                    {vm.m.proofRemove}
                  </span>
                </span>
              )}
            </div>
          </div>
          {/* The artboard's reach and lift, which the organisation reads and a
            supporter never does. */}
          {!isPublic && (
            <div className="Marketplace-Proof-itemStats">
              <div className="Marketplace-Proof-itemReach">{u.reach}</div>
              <div className="Marketplace-Proof-itemReachNote">{u.reachNote}</div>
              {u.lift && (
                <div className="Marketplace-Proof-itemLift">{u.lift}</div>
              )}
            </div>
          )}
        </div>
        {editing && this.renderPostForm(vm, p, true, u)}
      </div>
    );
  }

  renderFeed(vm, p, isPublic) {
    return (
      <div className="Marketplace-card">
        {this.renderHead(
          vm.m.proofPart3,
          p.part3Note,
          p.postAddLabel,
          p.togglePostAdd,
          isPublic,
          p.postOpen
        )}
        {!isPublic && p.postOpen && this.renderPostForm(vm, p, false)}
        {/* A post that saved with files missing. It belongs here rather than
            in the composer, which has closed by the time this is known. */}
        {SHOW_ERRORS && !isPublic && (
          <ErrorPreview m={vm.m} className="Marketplace-Proof-formError" keys={["proofAttachmentsFailed"]} fill={{ n: 2 }} />
        )}
        {!isPublic && p.postWarning && (
          <div className="Marketplace-Proof-formError">{p.postWarning}</div>
        )}
        {this.renderBody(
          vm,
          p.updatesState,
          p.part3Empty,
          p.updates.length === 0,
          () => (
            <div className="Marketplace-Proof-list">
              {p.updates.map((u) => this.renderUpdate(vm, p, u, isPublic))}
            </div>
          )
        )}
      </div>
    );
  }

  /**
   * The head of the page a supporter lands on: the cover, who the
   * organisation is, the three figures it leads with, and the two controls a
   * supporter would press. The controls are deliberately inert — this is the
   * organisation looking at its own page, and "Add to my giving plan" is not
   * an action it can take on itself.
   */
  renderPublicProfile(vm, p) {
    const pub = p.publicProfile;
    return (
      <div className="Marketplace-Proof-publicCard">
        <div className="Marketplace-Proof-publicCover">
          {this.renderCoverInto(vm, p.cover, "Marketplace-Proof-coverImg", p.coverPosition)}
        </div>
        <div className="Marketplace-Proof-publicBody">
          <div
            className="Marketplace-Proof-publicMono"
            style={{ background: pub.monoBg, color: pub.monoFg }}
          >
            {pub.logo ? (
              <img
                src={pub.logo}
                alt={pub.name}
                className="Marketplace-Proof-publicMonoImg"
              />
            ) : (
              pub.mono
            )}
          </div>
          <div className="Marketplace-Proof-publicMain">
            <h3 className="Marketplace-Proof-publicName">
              {pub.name}
              {pub.verified && <span className="Marketplace-Proof-publicCheck">✓</span>}
            </h3>
            <div className="Marketplace-Proof-publicStats">
              {pub.stats.map((st) => (
                <div
                  className={
                    "Marketplace-Proof-publicStat" +
                    (st.lead ? " Marketplace-Proof-publicStat--lead" : "")
                  }
                  key={st.id}
                >
                  <div className="Marketplace-Proof-publicStatLabel">{st.label}</div>
                  <div className="Marketplace-Proof-publicStatValue">{st.value}</div>
                  <div className="Marketplace-Proof-publicStatNote">{st.note}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Inert in the organisation's own preview and live on the
              real page. The handler being absent is what decides it,
              so neither view can offer a press that does nothing. */}
          <div className="Marketplace-Proof-publicActions">
            <div
              className={
                "Marketplace-Proof-publicPrimary" +
                (pub.primaryActive ? " Marketplace-Proof-publicPrimary--on" : "") +
                (pub.onPrimary ? "" : " Marketplace-Proof-publicBtn--inert")
              }
              onClick={pub.onPrimary || undefined}
            >
              {pub.primaryLabel}
            </div>
            <div
              className={
                "Marketplace-Proof-publicSecondary" +
                (pub.secondaryActive ? " Marketplace-Proof-publicSecondary--on" : "") +
                (pub.onSecondary && !pub.secondaryBusy
                  ? ""
                  : " Marketplace-Proof-publicBtn--inert")
              }
              onClick={
                pub.secondaryBusy ? undefined : pub.onSecondary || undefined
              }
            >
              {pub.secondaryLabel}
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const vm = this.props.vm;
    const p = vm.proof;
    const isPublic = p.isPublicPreview;

    // There is no whole-page loading or error branch any more. Each card
    // carries its own, so a section that is slow or broken never decides what
    // the other three are allowed to show.
    return (
      <div className="Marketplace-Proof">
        {/* WHAT THIS DOES FOR FUNDING. The artboard put two measured claims
            here — "gave 34% more the following month" and "two businesses
            cited your latest story" — and neither figure exists anywhere in
            the product, so the copy was rewritten to the general point it
            was making rather than dropped. Nothing here is a statistic, so
            there is no number to be wrong. If readership against subsequent
            giving is ever measured, this is where it goes.

            Editing only: a supporter looking at the public preview is not
            the audience for advice about posting, which is why the artboard
            has it inside the nonprofit's own impact view. */}
        {!isPublic && (
          <div className="Marketplace-Proof-banner">
            <span className="Marketplace-Proof-bannerTitle">
              {vm.m.proofFundingTitle}
            </span>
            <span className="Marketplace-Proof-bannerBody">
              {this.renderFundingBody(vm)}
            </span>
          </div>
        )}

        {/* The supporter's view says what it is and how to leave, since the
            page it is standing in for has no editing furniture of its own. */}
        {isPublic && (
          <div className="Marketplace-Proof-publicBanner">
            <span className="Marketplace-Proof-publicBannerText">{p.publicBanner}</span>
            <div className="Marketplace-Proof-publicBack" onClick={p.togglePublic}>
              {p.publicToggleLabel}
            </div>
          </div>
        )}

        {/* Editing keeps the cover on its own, where the "Edit cover image"
            control belongs. A supporter meets it as the top of the profile
            card instead, which is the one place the two layouts differ. */}
        {isPublic ? this.renderPublicProfile(vm, p) : this.renderProfileCard(vm, p)}

        {/* The picker the cover modal drives. It lives out here so opening
            and closing the modal cannot lose the element mid-selection. */}
        <input
          className="Marketplace-Proof-fileInput"
          type="file"
          accept={COVER_IMAGE_TYPES.join(",")}
          ref={this.coverInput}
          onChange={this.onCoverPicked}
        />

        {this.renderGoal(vm, p, isPublic)}

        {this.renderMetrics(vm, p, isPublic)}

        {this.renderBuys(vm, p, isPublic)}

        {/* PART 3 · WHAT'S NEW is held back while the feed gets more testing
            (NOT_READY in Marketplace.js) — on the editing view and on the
            public one alike. isPublicPreview is the same flag for the
            nonprofit previewing its own page and for a supporter reading it,
            so there is no hiding one without the other; and a preview that
            showed what a visitor will not see would not be a preview. */}
        {!!p.showWhatsNew && this.renderFeed(vm, p, isPublic)}

        {!isPublic && p.coverModalOpen && this.renderCoverModal(vm, p)}
      </div>
    );
  }
}

NonprofitProof.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default NonprofitProof;
